import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import type {RootState} from '../store';

// Define a type for the slice state
interface Patient {
    id: number;
    firstName: string;
    lastName: string;
    fullName: string;
    birthdate: string;
}

interface PatientState {
    currentPatient: Patient;
    subPatients: Patient[];
}

// Define the initial state using that type
const initialState: PatientState = {
    currentPatient: {id: 0, firstName: '', lastName: '', fullName: '', birthdate: ''},
    subPatients: [],
};

export const patientSlice = createSlice({
    name: 'patient',
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        setCurrentPatient: (state, action: PayloadAction<Patient>) => {
            Object.assign(state.currentPatient, action.payload);
            // Persist current patient to localStorage for token refresh scenarios
            try {
                localStorage.setItem('currentPatient', JSON.stringify(action.payload));
            } catch {
                // Silently handle localStorage save errors
            }
        },
        setSubPatients: (state, action: PayloadAction<Patient[]>) => {
            //Object.assign(state.subPatients, action.payload);
            state.subPatients = action.payload;
        },
        restoreCurrentPatientFromStorage: (state) => {
            try {
                const storedPatient = localStorage.getItem('currentPatient');
                if (storedPatient) {
                    const patient = JSON.parse(storedPatient);
                    // Validate the stored patient has required fields
                    if (patient.id && patient.id > 0 && patient.firstName && patient.lastName) {
                        Object.assign(state.currentPatient, patient);
                    }
                }
            } catch {
                // Silently handle localStorage restore errors
            }
        },
        resetPatientState: (state) => {
            state.currentPatient = {id: 0, firstName: '', lastName: '', fullName: '', birthdate: ''};
            state.subPatients = [];
        },
    },
});

export const {setCurrentPatient, setSubPatients, restoreCurrentPatientFromStorage, resetPatientState} =
    patientSlice.actions;

export const selectCurrentPatient = (state: RootState) => state.patient.currentPatient;
export const selectFullname = (state: RootState) => state.patient.currentPatient?.fullName;
export const selectSubPatients = (state: RootState) => state.patient.subPatients;

export default patientSlice.reducer;
